'use client'

import { Suspense, useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { isNativePlatform } from '@/lib/utils/platform'
import { hasUnsentWork } from '@/lib/utils/unsent-work'
import { initKeyboardHandling } from '@/lib/utils/keyboard'
import { BrandedLoader } from '@/components/ui/branded-loader'
import { useNativeBackButton } from '@/hooks/use-native-back-button'
import { useNativeDeepLinks } from '@/hooks/use-native-deep-links'
import { useOnResume } from '@/hooks/use-on-resume'
import { setSystemBarsForLightPages, setSystemBarsForLoader } from '@/lib/native/system-bars'
import { RouteRememberer } from '@/components/mobile/route-rememberer'
import { NativeSessionBackup } from '@/components/mobile/native-session-backup'

/**
 * Native app loading shell.
 *
 * On native platforms: SSR-renders the BrandedLoader as the FIRST PAINT so
 * there's no white/cream gap between the iOS LaunchScreen / Android splash
 * teardown and React hydration. The loader covers the cream `--background`
 * body until we're confident the next route's content has painted, then
 * fades out. The native SplashScreen plugin's launch overlay is disabled
 * (launchShowDuration: 0); the WebView background color (set via
 * capacitor.config.ts ios.backgroundColor + android.backgroundColor) is
 * the layer that prevents the white flash during WebView load.
 *
 * Also wires:
 * - Android hardware back button → router.back()
 * - Universal Links, App Links and `alumnia://` links → router.push (ALU-221)
 * - Global keyboard handling (scroll-into-view, tap-to-dismiss, iOS Done bar)
 *
 * On web: renders children immediately with no overhead.
 */
/** Away at least this long and the screen is refreshed on return. */
const RESUME_REFRESH_AFTER_MS = 2 * 60 * 1000

export function NativeAppShell({
  children,
  isNative = false,
  loader,
}: {
  children: React.ReactNode
  /**
   * The first-paint splash. Defaults to the classic BrandedLoader; the root
   * layout passes the Encuentro one in Encuentro (ALU-115). The shell's
   * timing (show, hide the native splash, fade) is the same for both.
   */
  loader?: React.ReactNode
  /**
   * Server-side native UA detect (from `app/layout.tsx`). Used as the
   * INITIAL state of `showLoader` so the SSR markup includes the loader
   * for native users — eliminates the cream-body flash that would
   * otherwise happen between WebView load and `useEffect` running. Must
   * match the client's view of the UA so React's hydration is consistent;
   * we propagate via prop so server and client agree.
   */
  isNative?: boolean
}) {
  // SSR-render the loader on native so the first paint is brand-blue + logo.
  // On web the prop is false → no SSR content shift, no flash.
  const [showLoader, setShowLoader] = useState(isNative)
  const [isFadingOut, setIsFadingOut] = useState(false)

  // Wire Android back button globally
  useNativeBackButton()

  // Open Universal Links / App Links / alumnia:// links (cold and warm start)
  useNativeDeepLinks()

  // Back from the background after a couple of minutes: the server-rendered screen
  // (feed, counters) is stale, and a WebView has no pull-to-refresh. Client state
  // (drafts, scroll) survives router.refresh(); it is skipped while the person
  // has text unsent or an upload running (bug bash SW-1).
  const router = useRouter()
  useOnResume(
    () => {
      if (isNativePlatform() && !hasUnsentWork(window)) router.refresh()
    },
    { minAwayMs: RESUME_REFRESH_AFTER_MS }
  )

  // ── Keyboard handling (native only) ─────────────────────────────
  // Enables: scroll-into-view on focus, tap-to-dismiss, iOS Done bar.
  // Must be called once at root level — cleanup removes all listeners.
  useEffect(() => {
    if (!isNativePlatform()) return

    let cleanup: (() => void) | undefined
    let cancelled = false

    initKeyboardHandling().then((fn) => {
      if (cancelled) {
        // Component unmounted before init resolved — clean up immediately
        fn()
      } else {
        cleanup = fn
      }
    })

    return () => {
      cancelled = true
      cleanup?.()
    }
  }, [])

  useEffect(() => {
    if (!isNativePlatform()) {
      // SSR UA-detect said native (Capacitor regex matched) but the client
      // bridge is absent — cached HTML served to a web client, UA spoof,
      // in-app browser with capacitor in UA but no bridge, etc. Without
      // this reconciliation, `showLoader` stays true forever and a fixed
      // z-9999 overlay covers the entire app. Snap to client truth.
      setShowLoader(false)
      return
    }

    // Step 1: Show BrandedLoader immediately (it renders behind native splash)
    setShowLoader(true)
    // A web reload shows the blue loader again: white icons until it fades (ALU-403).
    void setSystemBarsForLoader()

    // Step 2: Wait for BrandedLoader to actually paint, then hide native splash
    const frameId = requestAnimationFrame(() => {
      // Double-rAF ensures the browser has actually painted the BrandedLoader
      requestAnimationFrame(async () => {
        try {
          const { SplashScreen } = await import('@capacitor/splash-screen')
          await SplashScreen.hide({ fadeOutDuration: 0 })
        } catch {
          // Splash screen plugin might not be available
        }

        // Start fading out the BrandedLoader after a brief hold
        setTimeout(() => {
          setIsFadingOut(true)
          setTimeout(() => {
            setShowLoader(false)
            void setSystemBarsForLightPages()
          }, 300)
        }, 600)
      })
    })

    return () => cancelAnimationFrame(frameId)
  }, [])

  return (
    <>
      {/* BrandedLoader overlay — fades out over content, then removes itself */}
      {showLoader && (
        <div
          className={`fixed inset-0 z-9999 transition-opacity duration-300 ${
            isFadingOut ? 'opacity-0' : 'opacity-100'
          }`}
        >
          {/* Logo-only — no text. Top global apps (Instagram, WhatsApp,
              Linear, Stripe Dashboard) all use a static brand mark on
              cold launch; "Loading…" is amateur. */}
          {loader ?? <BrandedLoader message={null} />}
        </div>
      )}

      {/* RouteRememberer wraps `useSearchParams` — must be inside a
          Suspense boundary in Next 15+/16 or the entire ancestor subtree
          is forced to client-render at runtime AND a "Missing Suspense
          boundary" warning floods Sentry on every nav. Renders nothing,
          so a `null` fallback is correct. */}
      <Suspense fallback={null}>
        <RouteRememberer />
      </Suspense>

      {/* Session re-entry (phase 2): secure backup of the refresh token, silent
          restore when the WebView lost its cookies, Face ID / fingerprint lock.
          Renders nothing on web. Logic: hooks/use-native-session-backup.ts. */}
      <NativeSessionBackup />

      {/* Children rendered directly — NO wrapper div, NO transforms.
          This is critical: on iOS WebKit, any parent with CSS `transform`
          breaks `position: fixed` on all descendants. */}
      {children}
    </>
  )
}
